import {
  describeChanges,
  groupEvents,
  sourceOf,
  type EventGroup,
  type Source,
} from '../timeline/timeline-format.js';
import { formatMinor, type AuditEventRecord, type ObjectRelation } from '@hvac/contracts';
import { Card, CardBody, CardHeader, Skeleton } from '@hvac/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  objectRelationshipsQuery,
  objectSummaryQuery,
  objectTimelineQuery,
} from '../../shared/api/object-queries.js';

/**
 * The shared capabilities of a business object: what surrounds it, and what
 * has happened to it. Any object page renders these two panels the same way,
 * the consistency is the point. Learn one object page, know them all.
 */

const RELATION_ROUTES: Record<string, { to: string; label: string } | undefined> = {
  transaction: { to: '/bookkeeping', label: 'Open bookkeeping' },
  document: { to: '/documents', label: 'Open inbox' },
  vendor: { to: '/suppliers', label: 'Open suppliers' },
};

export function RelationshipsPanel({
  organizationId,
  entityType,
  entityId,
}: {
  organizationId: string;
  entityType: 'vendor' | 'customer' | 'job' | 'document' | 'transaction' | 'equipment';
  entityId: string;
}) {
  const relations = useQuery(objectRelationshipsQuery(organizationId, entityType, entityId));

  return (
    <Card>
      <CardHeader
        title="Around this"
        description="Everything connected, counted from the records themselves."
      />
      <CardBody>
        {relations.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-4/5" />
          </div>
        ) : relations.data === undefined || relations.data.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nothing is connected to this yet. Connections appear as records, documents and jobs
            reference it.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {relations.data.map((relation) => (
              <RelationRow key={relation.relation} relation={relation} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function RelationRow({ relation }: { relation: ObjectRelation }) {
  const route = RELATION_ROUTES[relation.entityType];
  return (
    <li className="flex items-baseline justify-between gap-3 py-2 text-sm">
      <span className="text-content">
        <span className="tabular font-semibold">{relation.itemCount}</span>{' '}
        <span className="text-content-muted">{relation.relation.toLowerCase()}</span>
        {relation.totalMinor === null ? null : (
          <span className="text-content-muted"> · {formatMinor(relation.totalMinor)}</span>
        )}
        {relation.latestOn === null ? null : (
          <span className="text-content-subtle"> · last {relation.latestOn}</span>
        )}
      </span>
      {route === undefined ? null : (
        <Link to={route.to} className="shrink-0 text-xs text-brand-700 hover:underline">
          {route.label}
        </Link>
      )}
    </li>
  );
}

export function ObjectTimeline({
  organizationId,
  resourceType,
  resourceId,
}: {
  organizationId: string;
  resourceType: string;
  resourceId: string;
}) {
  const timeline = useQuery(objectTimelineQuery(organizationId, resourceType, resourceId));

  return (
    <Card>
      <CardHeader
        title="Timeline"
        description="Its life story, read straight from the audit trail. Nothing here can be edited, that is the point."
      />
      <CardBody>
        {timeline.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : timeline.data === undefined || timeline.data.items.length === 0 ? (
          <p className="text-sm text-content-muted">Nothing recorded yet.</p>
        ) : (
          <TimelineList events={timeline.data.items} />
        )}
      </CardBody>
    </Card>
  );
}

/** Events grouped by day, newest first, shared by object pages and ⌘T. */
export function TimelineList({ events }: { events: AuditEventRecord[] }) {
  const byDay = new Map<string, AuditEventRecord[]>();
  for (const event of events) {
    const day = event.occurredAt.slice(0, 10);
    const bucket = byDay.get(day);
    if (bucket === undefined) byDay.set(day, [event]);
    else bucket.push(event);
  }

  return (
    <div className="flex flex-col gap-5">
      {Array.from(byDay.entries()).map(([day, dayEvents]) => (
        <section key={day}>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
            {dayLabel(day)}
          </h3>
          <ul className="mt-2 flex flex-col gap-2 border-l border-border pl-4">
            {groupEvents(dayEvents).map((group) => (
              <TimelineEntry key={group.events[0]?.id ?? group.key} group={group} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const SOURCE_LABEL: Readonly<Record<Source, string>> = {
  person: '',
  automatic: 'filed automatically',
  system: 'the system',
};

/** One act, or a run of the same act by the same hand, with what moved. */
function TimelineEntry({ group }: { group: EventGroup }) {
  const first = group.events[0];
  if (first === undefined) return null;
  const source = sourceOf(first);
  const who = first.actorEmail ?? SOURCE_LABEL[source];
  const changes = group.events.length === 1 ? describeChanges(first) : [];
  const count = group.events.length;

  return (
    <li className="text-sm">
      <span className="text-content">
        {count > 1 ? `${String(count)} × ` : ''}
        {first.summary}
      </span>
      <span className="text-content-subtle">
        {' '}
        · {first.occurredAt.slice(11, 16)}
        {who === '' ? '' : ` · ${who}`}
      </span>
      {changes.length === 0 ? null : (
        <ul className="mt-1 flex flex-col gap-0.5 pl-3 text-xs text-content-muted">
          {changes.map((change) => (
            <li key={change.field}>
              {change.field}: {change.from} → {change.to}
            </li>
          ))}
        </ul>
      )}
      {count > 1 ? (
        <details className="mt-1 text-xs text-content-muted">
          <summary className="cursor-pointer">Show each</summary>
          <ul className="mt-1 flex flex-col gap-0.5 pl-3">
            {group.events.map((event) => (
              <li key={event.id}>{event.occurredAt.slice(11, 19)}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  );
}

function dayLabel(day: string): string {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const yesterday = new Date(today.getTime() - 86_400_000).toISOString().slice(0, 10);
  if (day === todayIso) return 'Today';
  if (day === yesterday) return 'Yesterday';
  return day;
}

/* ------------------------------------------------------------------------- */

const COST_LABELS: Record<string, string> = {
  material: 'materials',
  labour: 'labour',
  subcontract: 'subcontractors',
  equipment: 'equipment',
  permit: 'permits',
  travel: 'travel',
  overhead: 'overhead',
  warranty: 'warranty work',
  other: 'other costs',
};

/**
 * Composes the at-a-glance sentence from deterministic facts. The numbers came
 * from SQL over confirmed records; this function only arranges them into
 * English. If a sentence here ever needs a number the facts don't carry, the
 * fact belongs in object_summary first.
 */
function composeSummary(
  entityType: string,
  facts: {
    fact: string;
    amountMinor: string | null;
    observationCount: number;
    asOf: string | null;
  }[],
): string[] {
  const byFact = new Map(facts.map((entry) => [entry.fact, entry]));
  const lines: string[] = [];

  if (entityType === 'vendor') {
    const recent = byFact.get('spend_12mo');
    const prior = byFact.get('spend_prior_12mo');
    const typical = byFact.get('typical_purchase');
    if (recent !== undefined && recent.observationCount > 0 && recent.amountMinor !== null) {
      let line = `${formatMinor(recent.amountMinor)} across ${String(recent.observationCount)} confirmed records in the last 12 months`;
      if (prior !== undefined && prior.amountMinor !== null && BigInt(prior.amountMinor) > 0n) {
        const change =
          ((BigInt(recent.amountMinor) - BigInt(prior.amountMinor)) * 100n) /
          BigInt(prior.amountMinor);
        line +=
          change === 0n
            ? ', level with the year before'
            : change > 0n
              ? `, up ${String(change)}% on the year before`
              : `, down ${String(-change)}% on the year before`;
      }
      lines.push(line + '.');
    }
    if (typical !== undefined && typical.amountMinor !== null && typical.observationCount > 0) {
      lines.push(
        `A typical purchase is ${formatMinor(typical.amountMinor)}, over ${String(typical.observationCount)} records${typical.asOf === null ? '' : ` since ${typical.asOf}`}.`,
      );
    }

    // The judgement, in the words of the rule that produced it. The rules
    // live in object_summary; this only reads them out.
    const risk = facts.find((entry) => entry.fact.startsWith('risk_'));
    if (risk !== undefined) {
      const atRisk = risk.amountMinor === null ? null : formatMinor(risk.amountMinor);
      const RISK_SENTENCES: Record<string, string> = {
        risk_unverified_registration:
          atRisk === null
            ? 'Their GST/HST number has never been checked, and credits are being claimed against it.'
            : `${atRisk} of credits are claimed against a GST/HST number nobody has checked. Verify it before you file.`,
        risk_too_new: 'Too few records to judge this supplier yet. Ask again after a few more.',
        risk_tax_inconsistent:
          'More of their invoices arrive without tax than with it. Worth knowing why before claiming.',
        risk_ordinary: 'Nothing about this supplier needs attention.',
        risk_unknown: 'No confirmed records yet, so there is nothing to judge.',
      };
      const sentence = RISK_SENTENCES[risk.fact];
      if (sentence !== undefined) lines.push(sentence);
    }
  }

  if (entityType === 'customer') {
    const jobs = byFact.get('jobs_lifetime');
    const margin = byFact.get('job_margin_lifetime');
    if (jobs !== undefined && jobs.amountMinor !== null) {
      let line = `${String(jobs.observationCount)} job${jobs.observationCount === 1 ? '' : 's'}, ${formatMinor(jobs.amountMinor)} invoiced`;
      if (margin !== undefined && margin.amountMinor !== null) {
        line += `, ${formatMinor(margin.amountMinor)} margin`;
      }
      lines.push(line + '.');
    }
    const booked = byFact.get('booked_work');
    if (booked !== undefined && booked.amountMinor !== null && booked.observationCount > 0) {
      lines.push(
        `${formatMinor(booked.amountMinor)} of work booked and not yet earned across ${String(booked.observationCount)} job${booked.observationCount === 1 ? '' : 's'}${booked.asOf === null ? '' : `, the earliest on ${booked.asOf}`}.`,
      );
    }
    const billed = byFact.get('billed_12mo');
    if (billed !== undefined && billed.amountMinor !== null) {
      lines.push(
        `${formatMinor(billed.amountMinor)} billed in the last 12 months over ${String(billed.observationCount)} confirmed records.`,
      );
    }
  }

  if (entityType === 'job') {
    for (const entry of facts) {
      if (!entry.fact.startsWith('cost_') || entry.amountMinor === null) continue;
      const label = COST_LABELS[entry.fact.slice(5)] ?? entry.fact.slice(5);
      lines.push(
        `${formatMinor(entry.amountMinor)} on ${label} (${String(entry.observationCount)} cost entr${entry.observationCount === 1 ? 'y' : 'ies'}).`,
      );
    }
  }

  return lines;
}

export function AtAGlancePanel({
  organizationId,
  entityType,
  entityId,
}: {
  organizationId: string;
  entityType: 'vendor' | 'customer' | 'job';
  entityId: string;
}) {
  const summary = useQuery(objectSummaryQuery(organizationId, entityType, entityId));

  if (summary.isPending) {
    return (
      <Card>
        <CardHeader title="At a glance" description="Computed from confirmed records only." />
        <CardBody>
          <Skeleton className="h-6 w-3/4" />
        </CardBody>
      </Card>
    );
  }

  const lines = summary.data === undefined ? [] : composeSummary(entityType, summary.data);
  if (lines.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="At a glance"
        description="Computed from confirmed records only. Every number is re-derivable; nothing here is estimated."
      />
      <CardBody>
        <ul className="flex flex-col gap-1.5 text-sm text-content">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
